import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Mail,
  Phone,
  Calendar,
  MapPin,
  CreditCard,
  Briefcase,
  Hash,
  User,
  ShieldAlert,
  LogOut,
  Building,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { usePortalAuth } from "./PortalAuthContext";
import { LanguageSwitcherCard } from "./LanguageSwitcher";

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}.`;
  } catch {
    return iso;
  }
}

export default function PortalProfile() {
  const { employee, logout } = usePortalAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [discRecords, setDiscRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, d] = await Promise.all([
          api.get("/portal/profile"),
          api.get("/portal/disciplinary").catch(() => ({ data: { items: [] } })),
        ]);
        setProfile(p.data);
        setDiscRecords(d.data?.items || []);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || t("profile.loading_error"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/dolgozo/login", { replace: true });
  };

  const p = profile || employee || {};

  return (
    <div className="space-y-6 px-4 pb-8 pt-6 md:px-6 md:pt-8">
      <header>
        <p className="text-[14px] text-zinc-500">{t("profile.title")}</p>
      </header>

      {/* Identity card */}
      <section className="rounded-3xl bg-white p-6 text-center shadow-[0_1px_3px_0_rgba(0,0,0,0.05),0_2px_6px_-2px_rgba(0,0,0,0.04)] dark:bg-zinc-900">
        <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-[22px] font-semibold text-white shadow-[0_6px_18px_-4px_rgba(59,130,246,0.4)]">
          {initials(p)}
        </div>
        <h1 className="mt-4 text-[24px] font-semibold leading-tight tracking-tight">
          {p.titulus ? `${p.titulus} ` : ""}
          {p.name || "—"}
        </h1>
        <p className="mt-1 text-[13.5px] text-zinc-500">
          {p.position || "—"}
        </p>
        {p.site_name && (
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-3 py-1 text-[12px] font-medium text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
            <Building className="h-3 w-3" strokeWidth={2} />
            {p.site_name}
          </p>
        )}
      </section>

      {loading ? (
        <div className="space-y-3">
          {["s1", "s2", "s3"].map((k) => (
            <div key={k} className="h-[120px] animate-pulse rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900" />
          ))}
        </div>
      ) : (
        <div className="space-y-5">
          <Section title={t("profile.name")}>
            <Row icon={User} label={t("profile.last_name")} value={p.last_name} />
            <Row icon={User} label={t("profile.first_name")} value={p.first_name} />
            <Row icon={User} label={t("profile.birth_last_name")} value={p.birth_last_name} />
            <Row icon={User} label={t("profile.birth_first_name")} value={p.birth_first_name} />
            <Row icon={User} label={t("profile.mother_name")} value={p.mother_name} />
          </Section>

          <Section title={t("profile.birth")}>
            <Row icon={Calendar} label={t("profile.birth_date")} value={fmtDate(p.birth_date)} />
            <Row icon={MapPin} label={t("profile.birth_place")} value={p.birth_place} />
          </Section>

          <Section title={t("profile.contact")}>
            <Row icon={Mail} label={t("profile.email")} value={p.email} mono />
            <Row icon={Phone} label={t("profile.phone")} value={p.phone} mono />
          </Section>

          <Section title={t("profile.address")}>
            <Row icon={MapPin} label={t("profile.permanent_address")} value={p.address} />
            <Row icon={Mail} label={t("profile.mailing_address")} value={p.mailing_address || p.address} />
          </Section>

          <Section title={t("profile.identifiers")}>
            <Row icon={Hash} label={t("profile.taj_number")} value={p.taj} mono />
            <Row icon={Hash} label={t("profile.tax_id")} value={p.tax_id} mono />
            <Row icon={Hash} label={t("profile.id_card_number")} value={p.id_card_number} mono />
          </Section>

          <Section title={t("profile.bank_section")}>
            <Row icon={CreditCard} label={t("profile.bank_name")} value={p.bank_name} />
            <Row icon={Hash} label={t("profile.account_number")} value={p.bank_account_number} mono />
            <Row
              icon={Briefcase}
              label={t("profile.payment_method")}
              value={
                p.payment_method === "cash"
                  ? t("profile.payment_cash")
                  : p.payment_method === "transfer"
                  ? t("profile.payment_transfer")
                  : "—"
              }
            />
          </Section>

          {(p.pos_id || p.pos_password) && (
            <Section title={t("profile.pos_access")}>
              <Row icon={Hash} label="POS ID" value={p.pos_id} mono />
              <Row icon={Hash} label={t("profile.pos_password")} value={p.pos_password} mono />
            </Section>
          )}

          {discRecords.length > 0 && (
            <Section title={`${t("dashboard.disciplinary_record")} (${discRecords.length})`} alert>
              {discRecords.map((d, i) => (
                <div
                  key={d.id}
                  data-testid={`disc-${d.id}`}
                  className={"px-5 py-4 " + (i > 0 ? "border-t border-zinc-100 dark:border-zinc-800/60" : "")}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[13.5px] font-medium tracking-tight">
                        {d.reason || d.category || t("profile.entry")}
                      </p>
                      {d.notes && <p className="mt-1 text-[12px] text-zinc-500">{d.notes}</p>}
                      {d.issuer && <p className="mt-1 text-[11px] text-zinc-400">— {d.issuer}</p>}
                    </div>
                    <span className="shrink-0 font-mono text-[11.5px] tabular-nums text-zinc-400">
                      {d.issued_at || "—"}
                    </span>
                  </div>
                </div>
              ))}
            </Section>
          )}
        </div>
      )}

      <LanguageSwitcherCard />

      <button
        type="button"
        data-testid="portal-logout-bottom"
        onClick={handleLogout}
        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white py-3.5 text-[14px] font-semibold tracking-tight text-red-600 shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] transition hover:bg-red-50 dark:bg-zinc-900 dark:text-red-400 dark:hover:bg-red-950/30"
      >
        <LogOut className="h-4 w-4" strokeWidth={2} />
        {t("common.logout")}
      </button>
    </div>
  );
}

function initials(emp) {
  const src = `${emp?.last_name || ""} ${emp?.first_name || ""}`.trim() || emp?.name || "";
  if (!src) return "—";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Section({ title, children, alert }) {
  return (
    <section>
      <div className="mb-2.5 flex items-center gap-1.5 px-2">
        {alert && (
          <ShieldAlert className="h-3.5 w-3.5 text-red-500 dark:text-red-400" strokeWidth={2.2} />
        )}
        <h2 className="text-[13px] font-medium text-zinc-500">{title}</h2>
      </div>
      <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] dark:bg-zinc-900">
        {children}
      </div>
    </section>
  );
}

function Row({ icon: Icon, label, value, mono }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 px-5 py-3.5 first:pt-4 last:pb-4 [&:not(:first-child)]:border-t [&:not(:first-child)]:border-zinc-100 dark:[&:not(:first-child)]:border-zinc-800/60">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-zinc-100 text-zinc-500 dark:bg-zinc-800/60 dark:text-zinc-400">
        <Icon className="h-4 w-4" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11.5px] text-zinc-500">{label}</p>
        <p className={"mt-0.5 truncate text-[14px] tracking-tight " + (mono ? "font-mono tabular-nums" : "")}>
          {value}
        </p>
      </div>
    </div>
  );
}
